import { AgGridReact } from 'ag-grid-react';
import { logger } from '../../../services/logger/logger';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ClientSideRowModelModule,
  ColDef,
  DateFilterModule,
  EventApiModule,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  NumberFilterModule,
  PaginationModule,
  RowApiModule,
  TextFilterModule,
  ValidationModule,
} from 'ag-grid-community';

import { SetFilterModule } from 'ag-grid-enterprise';
import { useAgGridTheme } from '../../../hooks/useAgGridTheme';
import {
  closeAllFilterPopups as closeAllFilterPopupsImpl,
  getDynamicRowHeight as calcDynamicRowHeight,
  mapGridColumnDefs,
  getResponsiveHeaderHeight,
} from '../../../utils/agGridHelpers';
import {
  AgGridShell,
  useContainerResizeObserver,
  useFlexSizing,
  useGridPopupAutoClose,
  useRowHeightAutoUpdate,
  useWindowWidth,
} from './AgGridShell';
import CustomLoading from './CustomLoading';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  SetFilterModule,
  PaginationModule,
  RowApiModule,
  EventApiModule,
  ValidationModule,
]);

interface ClientSideAgGridProps {
  columnDefs?: ColDef[];
  rowData?: any[];
  loading?: boolean;
  onCellClicked?: (_params: any) => void;
  themeParams?: any;
  useDynamicRowHeight?: boolean; // Whether to use dynamic row height (default: true)
  className?: string;
  onFiltersActiveChange?: (_active: boolean) => void; // notify parent if any filter is applied
  resetFiltersSignal?: number | string | boolean; // when this changes, clear all filters
  infiniteRows?: boolean; // When true, disable pagination and fill available height
  getRowId?: (_params: any) => any;
  isInModal?: boolean; // Whether this grid is inside a modal (for proper z-index handling)
  domLayout?: 'normal' | 'autoHeight' | 'print';
}

export const CustomTooltip = (props: any) => {
  const muiTheme = useTheme();
  const { value, colDef } = props;

  if (colDef?.field === 'actions' || !value) return null;

  return (
    <div
      className="custom-ag-tooltip"
      style={{
        maxWidth: 300,
        minWidth: 100,
        background: muiTheme.palette.common.white,
        color: muiTheme.palette.text.primary,
        padding: '6px 10px',
        borderRadius: 6,
        boxShadow: muiTheme.palette.shadow.cardShadow,
        fontSize: '12px',
        lineHeight: '1.4',
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        zIndex: muiTheme.zIndex.agGridOverlay,
      }}
    >
      {value}
    </div>
  );
};

const ClientSideAgGrid = React.forwardRef<AgGridReact<any>, ClientSideAgGridProps>(
  (
    {
      columnDefs,
      rowData,
      loading,
      onCellClicked,
      themeParams,
      useDynamicRowHeight = true,
      className = '',
      onFiltersActiveChange,
      resetFiltersSignal,
      infiniteRows = false,
      getRowId,
      isInModal = false,
      domLayout,
    },
    forwardedRef,
  ) => {
    const muiTheme = useTheme();
    const [gridApi, setGridApi] = useState<GridApi | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [totalPages, setTotalPages] = useState(0);
    const windowWidth = useWindowWidth();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState<number | null>(null);
    const [rowHeight, setRowHeight] = useState(50);
    const [totalRecords, setTotalRecords] = useState(0);
    const [tableHeight, setTableHeight] = useState<number>(300);

    useEffect(() => {
      if (gridApi) {
        setTotalRecords(gridApi.getDisplayedRowCount());
      }
    }, [gridApi]);

    // Dynamic row height based on container height and page size (shared helper)
    const getDynamicRowHeight = useCallback(() => {
      if (!containerRef.current) return 50; // fallback height
      const containerHeight = containerRef.current.clientHeight;
      const availableHeight = containerHeight - getResponsiveHeaderHeight();
      return calcDynamicRowHeight(availableHeight, pageSize);
    }, [pageSize]);

    // Calculate responsive row height based on screen size (fallback)
    const getResponsiveRowHeight = useCallback(() => {
      if (windowWidth >= 1920) return 46; // Large screens
      if (windowWidth >= 1440) return 44; // Medium-large screens
      if (windowWidth >= 1024) return 42; // Medium screens
      if (windowWidth >= 768) return 40; // Small screens
      return 38; // Mobile screens
    }, [windowWidth]);

    const handleFilterOpened = useCallback((params: any) => {
      const col = params?.column;
      const width = (col && col.getActualWidth && col.getActualWidth()) || 0;
      if (!containerRef.current || !width) return;

      const apply = () => {
        // Get all popups and use the last one (most recent)
        const popups = document.body.querySelectorAll('.ag-popup');
        const popup = popups[popups.length - 1] as HTMLElement | undefined;
        if (!popup) return;

        // Target multiple elements within the popup to ensure width is applied
        const elementsToResize = [
          popup,
          popup.querySelector('.ag-filter') as HTMLElement | null,
          popup.querySelector('.ag-filter-wrapper') as HTMLElement | null,
          popup.querySelector('.ag-set-filter') as HTMLElement | null,
          popup.querySelector('.ag-set-filter-list') as HTMLElement | null,
        ].filter(Boolean);

        elementsToResize.forEach((element) => {
          if (element) {
            element.style.setProperty('width', `${width}px`, 'important');
            element.style.setProperty('max-width', `${width}px`, 'important');
            element.style.setProperty('min-width', `${width}px`, 'important');
          }
        });
      };

      if (typeof requestAnimationFrame !== 'undefined') {
        requestAnimationFrame(apply);
      } else {
        setTimeout(apply, 0);
      }
    }, []);

    const closeAllFilterPopups = useCallback(
      () => closeAllFilterPopupsImpl(gridApi as GridApi),
      [gridApi],
    );

    // Handle cell clicked - close popups and call user's callback
    const handleCellClicked = useCallback(
      (params: any) => {
        // Always close filter popups when any cell is clicked
        closeAllFilterPopups();

        // Call the user's onCellClicked callback if provided
        if (onCellClicked) {
          onCellClicked(params);
        }
      },
      [closeAllFilterPopups, onCellClicked],
    );

    const theme = useAgGridTheme({
      scopeId: className || 'default',
      rowHeight: useDynamicRowHeight ? rowHeight : getResponsiveRowHeight(),
      themeParams: themeParams ?? {},
      isInModal,
      clickable: !!onCellClicked,
    });

    // Update pagination data
    useEffect(() => {
      if (gridApi) {
        updatePaginationData(gridApi);
      }
    }, [pageSize, gridApi]);

    const { t } = useTranslation();

    // Add placeholders to floating filter inputs
    useEffect(() => {
      setTimeout(() => {
        document.querySelectorAll('.ag-floating-filter-input').forEach((el: any) => {
          if (!el?.disabled) el.setAttribute('placeholder', t('common.search'));
        });
      }, 0);
    }, [t]);

    // Close all background popups when a modal grid mounts
    useEffect(() => {
      if (isInModal) {
        // Close all AG Grid filter popups from background grids when modal opens
        const closeAllBackgroundPopups = () => {
          // Hide all existing popups
          const popups = document.body.querySelectorAll('.ag-popup');
          popups.forEach((popup) => {
            const popupElement = popup as HTMLElement;
            // Hide immediately
            popupElement.style.display = 'none';
            // Remove after a short delay to allow AG Grid to clean up
            setTimeout(() => {
              if (popupElement.parentNode) {
                try {
                  popupElement.parentNode.removeChild(popupElement);
                } catch (error) {
                  logger.error('Error removing popup element:', error as Error);
                }
              }
            }, 50);
          });
        };

        // Execute immediately when modal grid mounts
        closeAllBackgroundPopups();
      }
    }, [isInModal]);

    useGridPopupAutoClose(containerRef, gridApi, closeAllFilterPopups);

    useContainerResizeObserver(
      containerRef,
      (cw) => {
        setContainerWidth(cw);
        if (useDynamicRowHeight) {
          setRowHeight(getDynamicRowHeight());
        }
      },
      true,
    );

    useRowHeightAutoUpdate(
      useDynamicRowHeight,
      getDynamicRowHeight,
      `${pageSize}-${containerWidth ?? 'na'}`,
      setRowHeight,
    );

    // Track table (container) height in state and AG context
    useEffect(() => {
      if (containerRef.current) {
        const timeoutId = setTimeout(() => {
          // Check again inside timeout as ref may have become null
          if (containerRef.current) {
            setTableHeight(containerRef.current.clientHeight);
          }
        }, 150); // after mount/resize
        return () => clearTimeout(timeoutId);
      }
    }, [containerWidth, pageSize]);

    // Respond to parent-triggered filter reset
    useEffect(() => {
      if (!gridApi) return;
      try {
        const model = gridApi.getFilterModel?.() as Record<string, any> | null | undefined;
        const hasFilters = !!(model && Object.keys(model).length > 0);
        if (hasFilters) {
          gridApi.setFilterModel(null);
          // Ensure grid updates any internal state
          (gridApi as any).onFilterChanged?.();
          // Reset pagination to first page
          gridApi.paginationGoToFirstPage?.();
          setCurrentPage(1);
          updatePaginationData(gridApi);
          // Notify parent
          onFiltersActiveChange?.(false);
        }
      } catch (error) {
        logger.error('Error resetting filters:', error as Error);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetFiltersSignal]);

    // Handle grid ready
    const handleGridReady = (params: GridReadyEvent) => {
      setGridApi(params.api);
      updatePaginationData(params.api);

      if (useDynamicRowHeight) {
        window.requestAnimationFrame(() => {
          const newRowHeight = getDynamicRowHeight();
          setRowHeight(newRowHeight);
        });
      }

      const emitFiltersActive = (api: GridApi) => {
        try {
          const model = api.getFilterModel?.() as Record<string, any> | null | undefined;
          const active = !!(model && Object.keys(model).length > 0);
          onFiltersActiveChange?.(active);
        } catch (error) {
          onFiltersActiveChange?.(false);
          logger.error('Error getting filter model:', error as Error);
        }
      };

      // Emit initial state
      emitFiltersActive(params.api);

      (params as any)?.api.addEventListener('filterChanged', () => {
        updatePaginationData(params.api);
        setCurrentPage(1);
        emitFiltersActive(params.api);
        setTotalRecords(params.api.getDisplayedRowCount());
      });

      (params as any)?.api.addEventListener('paginationChanged', () => {
        updatePaginationData(params.api);
        setTotalRecords(params.api.getDisplayedRowCount());
      });
    };

    // Update pagination data
    const updatePaginationData = (api: GridApi) => {
      const currentPageFromGrid = api.paginationGetCurrentPage() + 1;
      setTotalPages(api.paginationGetTotalPages());
      setCurrentPage(currentPageFromGrid);
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize: number) => {
      setPageSize(newPageSize);
      setCurrentPage(1);
      if (gridApi) {
        gridApi.paginationGoToPage(0);
        updatePaginationData(gridApi);
      }
    };

    // Handle page change
    const handlePageChange = (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
        if (gridApi) {
          gridApi.paginationGoToPage(page - 1);
        }
      }
    };

    // Calculate total minimum width
    const { applyFlexSizing } = useFlexSizing(columnDefs || [], containerWidth);

    return (
      <AgGridShell
        className={className}
        gridHeight={infiniteRows ? '100%' : 'calc(100% - 40px)'}
        containerStyle={{
          backgroundColor: muiTheme.palette.common.white,
          borderRadius: `${muiTheme.borderRadius.xl}px`,
          boxShadow: muiTheme.palette.shadow.cardContent,
        }}
        containerRef={containerRef}
        showPagination={!infiniteRows}
        paginationProps={
          !infiniteRows
            ? {
                page: currentPage - 1,
                pageCount: totalPages,
                pageSize,
                onPageChange: ({ page, pageSize: newPageSize }) => {
                  if (newPageSize !== pageSize) {
                    handlePageSizeChange(newPageSize);
                  }
                  handlePageChange(page + 1);
                },
                onPageSizeChange: ({ pageSize: newPageSize }) => {
                  handlePageSizeChange(newPageSize);
                },
                totalRecords,
              }
            : undefined
        }
      >
        <AgGridReact
          columnDefs={mapGridColumnDefs(columnDefs || [], {
            applyFlexSizing,
            tooltipComponent: CustomTooltip,
            containerHeight: tableHeight,
            suppressHeaderFocus: true,
            suppressReactComponentReloading: true,
            // Allow all keyboard events in filter popups (search in modals)
            suppressKeyboardEvent: () => false,
            defaultWidth: 120,
            getTooltipWidth: ({ rest }) => ((rest as any)?.maxWidth as number) || 150,
          })}
          rowData={rowData}
          pagination={!infiniteRows}
          paginationPageSize={infiniteRows ? (undefined as any) : pageSize}
          onGridReady={handleGridReady}
          onFilterOpened={handleFilterOpened as any}
          onCellClicked={handleCellClicked}
          theme={theme}
          loading={loading}
          loadingOverlayComponent={() => <CustomLoading />}
          suppressPaginationPanel={true}
          tooltipShowDelay={300}
          tooltipHideDelay={5000}
          suppressAggFuncInHeader={true}
          context={{ containerHeight: tableHeight }}
          popupParent={document.body}
          getRowId={getRowId ? getRowId : undefined}
          ref={forwardedRef as any}
          domLayout={domLayout}
        />
      </AgGridShell>
    );
  },
);

export default ClientSideAgGrid;
